import React from 'react';
import PropTypes from 'prop-types';
import { AppDialog } from '../../../core/Dialog/AppDialog';
import T from 'i18n-react';
import { withSnackbar } from 'notistack';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import {
  createRecaptchaVerifier,
  linkOrUpdatePhoneNumber,
  signInWithPhoneNumber,
  verifyPhoneAuthCode,
} from '../../../core/firebase/auth';
import { API_USER_CUSTOM_TOKEN, API_USER_PHONE } from '../../routing';
import { getFbPhoneErrorText } from '../../../core/firebase/error';
import PhoneInputField from './PhoneInputField';
import { JAPAN_CODE } from './variables';
import { fetchJson } from '../../../core/fetch';
import observeUserAuthChange from '../../../core/firebase/observeUserAuthChange';

const SEND_BUTTON_ID = 'tel-signin-button';

const styles = (theme) => ({
  contentRoot: {
    '& > *': {
      marginBottom: theme.spacing(1.5),
    },
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
  textField: {
    backgroundColor: theme.palette.common.white,
  },
  button: {
    width: '100%',
    maxWidth: 200,
    [theme.breakpoints.up('sm')]: {
      minWidth: 100,
      maxWidth: 100,
    },
  },
  typography: {
    fontSize: theme.typography.pxToRem(15),
  },
});

class PhoneDialog extends AppDialog {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      signedIn: false,
      confirm: false,
      confirmationCode: { value: '', valid: false },
      confirmationResult: null,
      appVerifier: null,
    };
  }

  unsubscribe = null;
  messageKey = null;

  componentDidMount() {
    super.componentDidMount();

    this.unsubscribe = observeUserAuthChange(API_USER_CUSTOM_TOKEN)(
      this.handleUserState,
      this.handleUserStateError,
    );
  }

  componentWillUnmount() {
    this.unsubscribe?.();
    super.componentWillUnmount();
  }

  getTitle = () => T.translate('editX', { x: T.translate('user.phoneNumber') });

  getPropPhoneNumber = () => {
    const { phoneNumber, countryCode } = this.props;
    if (phoneNumber && countryCode) {
      return phoneNumber.replace(
        new RegExp(`^\\+${countryCode.replace(/^\+/, '')}`),
        '',
      );
    }
    return phoneNumber;
  };

  isValidData = () => {
    const {
      phoneNumber = { valid: false },
      countryCode = { valid: false },
      verifiedAt,
      confirm = false,
    } = this.state;

    // Allow to send the confirmation code when phone number hasn't verified
    if (!verifiedAt && !confirm && phoneNumber.valid && countryCode.valid) {
      return true;
    }

    const codeChanged =
      countryCode.valid && this.props.countryCode !== countryCode.value;
    const numberChanged =
      phoneNumber.valid && phoneNumber.value !== this.getPropPhoneNumber();
    return (
      ((codeChanged || countryCode.valid) && numberChanged) ||
      (codeChanged && (numberChanged || phoneNumber.valid))
    );
  };

  isActionButtonDisabled(): boolean {
    const { signedIn, appVerifier, confirm, confirmationCode } = this.state;
    return (
      super.isActionButtonDisabled() ||
      !signedIn ||
      !this.isValidData() ||
      !((confirm && confirmationCode.valid) || (!confirm && appVerifier))
    );
  }

  getDialogProps = () => ({ disableBackdropClick: true });

  handleUserState = (user) => {
    if (this._mounted) {
      if (user) {
        this.messageKey && this.closeSnackbar(this.messageKey);
        this.messageKey = null;
      } else {
        this.messageKey = this.enqueueSnackbar(T.translate('waitLoading'), {
          variant: 'warning',
          persist: true,
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      }
      this.setState({ signedIn: user != null });
    }
  };

  handleUserStateError = () =>
    this._mounted &&
    this.enqueueSnackbar(T.translate('errorRefresh'), {
      variant: 'error',
      persist: true,
      anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
    });

  handleOpen(event) {
    const { countryCode, verifiedAt } = this.props;
    const propPhoneNumber = this.getPropPhoneNumber();
    this.setState({
      open: true,
      confirm: false,
      phoneNumber: { value: propPhoneNumber || '', valid: !!propPhoneNumber },
      countryCode: { value: countryCode || JAPAN_CODE, valid: true },
      verifiedAt,
    });
  }

  handleClose(event) {
    this.setState({ open: false });
  }

  handleAccept(event): undefined {
    const {
      phoneNumber,
      countryCode,
      confirmationResult,
      confirmationCode,
      confirm,
      appVerifier,
    } = this.state;
    this.setState({ progress: true });

    if (confirm) {
      const credential = verifyPhoneAuthCode(
        confirmationResult,
        confirmationCode.value,
      );
      linkOrUpdatePhoneNumber(credential)
        .then((idToken) => {
          if (this._mounted) {
            fetchJson(API_USER_PHONE, {
              init: { method: 'PATCH' },
              jsonBody: {
                user: { token: idToken, country_code: countryCode.value },
              },
              onSuccess: ({ json }) => {
                this.handleSuccess({
                  message: T.translate('savedX', {
                    x: T.translate('user.phoneNumber'),
                  }),
                  severity: 'success',
                });
                this.props.onSuccess?.(json);
              },
              onError: ({ messages = [] }) => {
                this.handleError({
                  message:
                    messages?.length > 0
                      ? messages
                      : [T.translate('request.error')],
                });
              },
            });
          }
        })
        .catch((error) => {
          if (this._mounted) {
            this.handleError({
              message: getFbPhoneErrorText(error.code),
              snackbarProps: {
                variant: 'error',
                persist: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
              },
            });
          }
        });
    } else {
      signInWithPhoneNumber(
        `+${countryCode.value}${phoneNumber.value}`,
        appVerifier,
      )
        .then((confirmationResult) => {
          this.handleSuccess({
            open: true,
            otherState: {
              confirm: true,
              confirmationCode: { value: '', valid: false },
              confirmationResult,
            },
          });
        })
        .catch((error) => {
          if (this._mounted) {
            this.handleError({
              message: getFbPhoneErrorText(error.code),
              snackbarProps: {
                variant: 'error',
                persist: true,
                anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
              },
            });
          }
        });
    }
  }

  dialogContentRenderer = () => {
    const { classes } = this.props;
    const {
      phoneNumber = { value: '', valid: false },
      countryCode = { value: '', valid: false },
      confirmationCode = { value: '', valid: false },
      confirm,
    } = this.state;

    if (confirm) {
      return (
        <div className={classes.contentRoot}>
          <Typography className={classes.typography}>
            {T.translate('users.phoneNumbers.confirmInstruction', {
              phoneNumber: phoneNumber.value,
            })}
          </Typography>
          <Typography className={classes.typography} color="error">
            {T.translate('users.phoneNumbers.warning')}
          </Typography>
          <TextField
            type="number"
            inputMode="numeric"
            value={confirmationCode.value}
            variant="outlined"
            className={classes.textField}
            fullWidth
            required
            onChange={(event) =>
              this.setState({
                confirmationCode: {
                  value: event.target.value,
                  valid: event.target.checkValidity(),
                },
              })
            }
          />
        </div>
      );
    } else {
      return (
        <PhoneInputField
          messages={[
            T.translate('users.phoneNumbers.inputInstruction', {
              buttonText: T.translate('next'),
            }),
            T.translate('users.phoneNumbers.countryCodeInstruction'),
            T.translate('users.phoneNumbers.useDescription'),
          ]}
          countryCode={{
            ...countryCode,
            onChange: (event) =>
              this.setState({
                countryCode: {
                  value: event.target.value,
                  valid: event.target.checkValidity(),
                },
              }),
          }}
          phoneNumber={{
            ...phoneNumber,
            onChange: (event) =>
              this.setState({
                phoneNumber: {
                  value: event.target.value,
                  valid: event.target.checkValidity(),
                },
              }),
          }}
        />
      );
    }
  };

  handleRecaptchaButton = (element) => {
    this.setState((prevState) => {
      prevState.appVerifier?.clear?.();
      const appVerifier = element ? createRecaptchaVerifier(element) : null;
      return { appVerifier };
    });
  };

  getActionButtonProps = () => {
    if (this.state.confirm) {
      // Set key to clear ReCaptcha
      return { key: 'verifyButton' };
    } else {
      return { id: SEND_BUTTON_ID, ref: this.handleRecaptchaButton };
    }
  };

  getActionButtonText = () => T.translate(this.state.confirm ? 'save' : 'next');

  getCancelButtonProps = () => {
    if (this.state.confirm) {
      return { onClick: () => this.setState({ confirm: false }) };
    } else {
      return {};
    }
  };

  getCancelButtonText = () =>
    T.translate(this.state.confirm ? 'back' : 'close');
}

PhoneDialog.propTypes = {
  ...AppDialog.propTypes,
  phoneNumber: PropTypes.string,
  countryCode: PropTypes.string,
  verifiedAt: PropTypes.string,
};

export default withSnackbar(withStyles(styles)(PhoneDialog));
